import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from 'rxjs';
import {map} from "rxjs/operators";

import {UserPhoto} from '../classes/employee';
import {LeaveHistory} from './../classes/leave-history';
import {CommonTransformationService} from './common-transformation.service';
import {UserType} from '../enums/user-type.enum';
import {UserParam} from '../classes/user-param';
import {NoTitlecaseApprover} from '../classes/common';
import {PositionDescription, QueryResult} from "@app/shared";
import {SelectItem} from "primeng/api";

@Injectable()
export class HrService {

  constructor(private http: HttpClient, private transform: CommonTransformationService) {
  }

  getApprovers(): Observable<any> {

    const url = "/hr/lookup/approvers?start=0&count=-1";
    return this.http.get<any>(url);
  }

  getApproversAsDropdown(returnObjValue?: boolean): Observable<SelectItem[]> {

    if (returnObjValue) {
      return this.getApprovers().pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list, null, true, NoTitlecaseApprover);
      }));
    } else {
      return this.getApprovers().pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list, "id", true, NoTitlecaseApprover);
      }));
    }
  }

  getDefaultEmployeeClass(): Observable<any> {
    const url = `/hr/employee-classes/default-class-id`;
    return this.http.get<any>(url);
  }

  getEmployeeClass(includeAll?: boolean): Observable<QueryResult> {

    let url = '/hr/employee-classes/search?start=0&count=-1';
    if (includeAll) {
      url += `&includeAll=true`;
    }
    return this.http.get<QueryResult>(url);
  }

  getEmployeeClassAsDropdown(includeAll?: boolean, returnObjValue?: boolean): Observable<SelectItem[]> {

    if (returnObjValue) {
      return this.getEmployeeClass(includeAll).pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list, null, true);
      }));
    } else {
      return this.getEmployeeClass(includeAll).pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list, "id", true);
      }));
    }
  }

  getOrgOfferLetter(employeeClassId: number): Observable<any> {
    const url = `/hr/org-offer-letters/search?employeeClass.id=${employeeClassId}&start=0&count=-1`;
    return this.http.get<any>(url);
  }

  getOrgOfferLetterAsDropDown(employeeClassId: number): Observable<any> {
    return this.getOrgOfferLetter(employeeClassId).pipe(map(res => {
      return this.transform.convertToDropDown("name", res.list, "id");
    }));
  }

  getOrgOfferLetterClauses(employeeClassId?: number, defaultEmployeeClassId?: number): Observable<QueryResult> {
    let url = "/hr/org-offer-letter-clauses/search?start=0&count=-1";

    if (employeeClassId) {
      url += `&employeeClass.id=${employeeClassId}`;
    }
    if (defaultEmployeeClassId) {
      url += `&employeeClass.id=${defaultEmployeeClassId}`;
    }
    return this.http.get<QueryResult>(url);
  }

  getPhotoData(elementId: string, contentType: string): any {
    let newPhoto = document.getElementById(elementId) as HTMLImageElement;
    let photoType = 'data:' + contentType + ';base64,';

    return newPhoto.src.substring(photoType.length, newPhoto.src.length);
  }

  saveLeaveHistory(leaveHistory: LeaveHistory): Observable<LeaveHistory> {
    const url = `/hr/employees/${leaveHistory.userId}`;

    if (leaveHistory.id) {
      return this.http.put<LeaveHistory>(url, leaveHistory);
    } else {
      return this.http.post<LeaveHistory>(url, leaveHistory);
    }
  }

  saveUserPhoto(photo: UserPhoto): Observable<UserPhoto> {
    const url = `/hr/users/${photo.userId}/photo`;

    if (photo.id) {
      return this.http.put<UserPhoto>(url, photo);
    } else {
      return this.http.post<UserPhoto>(url, photo);
    }
  }

  getPosition(positionDescriptionId: number): Observable<PositionDescription> {
    const url = `/hr/position-descriptions/${positionDescriptionId}`;
    return this.http.get<PositionDescription>(url);
  }

  getPositions(positionTitle?: string): Observable<QueryResult> {

    let url = "/hr/position-descriptions/search?start=0&count=-1&active=true";

    if (positionTitle) {
      url += `&positionTitle=${positionTitle}`;
    }
    return this.http.get<QueryResult>(url);
  }

  getPositionsAsDropDown(returnObjValue?: boolean): Observable<SelectItem[]> {

    if (returnObjValue) {
      return this.getPositions().pipe(map(res => {
        return this.transform.convertToDropDown("positionTitle", res.list);
      }));
    } else {
      return this.getPositions().pipe(map(res => {
        return this.transform.convertToDropDown("positionTitle", res.list, "id");
      }));
    }
  }

  searchUsers(userParam: UserParam): Observable<QueryResult> {
    let url = "/utils/user-lookup/name-search?start=0&count=-1";
    if (userParam.name) {
      url += `&name=${userParam.name}`;
    }

    if (userParam.active) {
      url += '&active=true';
    } else {
      if (userParam.active == false) {
        url += '&active=false';
      }
    }
    if (userParam.employeeNumber) {
      url += `&code=${userParam.employeeNumber}`;
    }
    if (userParam.locationId) {
      url += `&locationId=${userParam.locationId}`;
    }
    if (userParam.obsId) {
      url += `&obsId=${userParam.obsId}`;
    }
    if (userParam.employeeClassId) {
      url += `&employeeClassId=${userParam.employeeClassId}`;
    }
    if (userParam.subtype) {
      url += `&subtype=${userParam.subtype}`;
    } else {
      url += `&subtype=${UserType.All}`;
    }
    if (userParam.isResourceManagerNotSet) {
      url += `&isResourceManagerNotSet=${userParam.isResourceManagerNotSet}`;
    }

    return this.http.get<QueryResult>(url);
  }

  getSubordinateObsForSelf(): Observable<any> {
    const url = "/hr/subordinate-obs/self?start=0&count=-1";

    return this.http.get(url);
  }
}
